import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { Check, ExternalLink, Loader2, Plus, X } from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/utils';
import { Button } from '@/components/ui/button';
import { accountBillingApi } from '@/services/endpoints/account/billing';
import { ACCOUNT_BILLING_KEYS, ACCOUNT_ORG_KEYS } from '@/components/account/queryKeys';
import {
  DataRow,
  LoadingRows,
  Notice,
  Panel,
  SectionTitle,
  StatusPill,
  SUBSCRIPTION_LABEL,
  SUBSCRIPTION_TONE,
  formatDay,
  formatMoney,
} from '@/components/account/accountUi';
import type { BillingCycle, SubscriptionPlan } from '@/services/api/accountTypes';

function apiMessage(error: unknown, fallback: string): string {
  const axiosError = error as AxiosError<{ message?: string; error?: string }>;
  return axiosError?.response?.data?.message || axiosError?.response?.data?.error || fallback;
}

export default function AccountSubscription() {
  const queryClient = useQueryClient();
  const [cycle, setCycle] = useState<BillingCycle>('monthly');
  const [confirmingCancel, setConfirmingCancel] = useState(false);

  const { data: subscription, isLoading: loadingSubscription } = useQuery({
    queryKey: ACCOUNT_BILLING_KEYS.subscription,
    queryFn: accountBillingApi.getSubscription,
    staleTime: 60_000,
  });

  const { data: plans, isLoading: loadingPlans } = useQuery({
    queryKey: ACCOUNT_BILLING_KEYS.plans,
    queryFn: () => accountBillingApi.getPlans(),
    staleTime: 5 * 60_000,
  });

  const currency = subscription?.currency ?? plans?.[0]?.currency ?? 'cad';
  const basePlans = useMemo(
    () => (plans ?? []).filter((plan) => plan.planType === 'base' && plan.isActive),
    [plans]
  );
  const addonPlans = useMemo(
    () => (plans ?? []).filter((plan) => plan.planType === 'addon' && plan.isActive),
    [plans]
  );

  const activeBase = subscription?.items?.find((item) => item.planType === 'base');
  const activeAddons = subscription?.items?.filter((item) => item.planType === 'addon') ?? [];
  const activeAddonPlanIds = new Set(activeAddons.map((item) => item.planId));

  const refresh = () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: ACCOUNT_BILLING_KEYS.subscription }),
      queryClient.invalidateQueries({ queryKey: ACCOUNT_BILLING_KEYS.dashboard }),
    ]);

  const checkout = useMutation({
    mutationFn: (planId: string) =>
      accountBillingApi.createCheckoutSession({
        basePlanId: planId,
        billingCycle: cycle,
        successUrl: `${window.location.origin}/account/subscription?checkout=success`,
        cancelUrl: `${window.location.origin}/account/subscription?checkout=cancelled`,
      }),
    onSuccess: (url) => {
      window.location.href = url;
    },
    onError: (error) => toast.error(apiMessage(error, 'Could not start checkout.')),
  });

  const changePlan = useMutation({
    mutationFn: (planId: string) => accountBillingApi.changePlan({ toPlanId: planId }),
    onSuccess: async () => {
      await refresh();
      toast.success('Plan updated.');
    },
    onError: (error) => toast.error(apiMessage(error, 'Could not change the plan.')),
  });

  const addAddon = useMutation({
    mutationFn: (planId: string) => accountBillingApi.addAddon(planId),
    onSuccess: async () => {
      await refresh();
      toast.success('Add-on added.');
    },
    onError: (error) => toast.error(apiMessage(error, 'Could not add that add-on.')),
  });

  const removeAddon = useMutation({
    mutationFn: (itemId: string) => accountBillingApi.removeAddon(itemId),
    onSuccess: async () => {
      await refresh();
      toast.success('Add-on removed.');
    },
    onError: (error) => toast.error(apiMessage(error, 'Could not remove that add-on.')),
  });

  const cancel = useMutation({
    mutationFn: () => accountBillingApi.cancelSubscription(subscription!.id),
    onSuccess: async () => {
      await refresh();
      setConfirmingCancel(false);
      toast.success('Subscription cancelled. Access continues until the period ends.');
    },
    onError: (error) => toast.error(apiMessage(error, 'Could not cancel the subscription.')),
  });

  const openPortal = useMutation({
    mutationFn: () => accountBillingApi.createPortalSession(`${window.location.origin}/account/subscription`),
    onSuccess: (url) => window.open(url, '_blank', 'noopener'),
    onError: (error) => toast.error(apiMessage(error, 'Could not open the billing portal.')),
  });

  const price = (plan: SubscriptionPlan) =>
    cycle === 'yearly' ? plan.priceYearly : plan.priceMonthly;

  const busy = checkout.isPending || changePlan.isPending;

  return (
    <>
      <SectionTitle
        title="Subscription"
        description="Your plan, add-ons, and what happens at the end of the period."
        action={
          <div
            className="inline-flex rounded-lg border border-border bg-card p-0.5"
            role="group"
            aria-label="Billing cycle"
          >
            {(['monthly', 'yearly'] as BillingCycle[]).map((option) => (
              <button
                key={option}
                type="button"
                onClick={() => setCycle(option)}
                aria-pressed={cycle === option}
                className={cn(
                  'rounded-md px-3 py-1.5 text-sm font-medium capitalize transition-colors',
                  cycle === option
                    ? 'bg-brand-cyan/10 text-foreground'
                    : 'text-muted-foreground hover:text-foreground'
                )}
              >
                {option}
              </button>
            ))}
          </div>
        }
      />

      <div className="space-y-6">
        {subscription?.status === 'cancelled' && (
          <Notice tone="warn" title="Subscription cancelled">
            Access continues until {formatDay(subscription.currentPeriodEnd)}. Choosing a plan below
            starts a new subscription.
          </Notice>
        )}

        <Panel title="Current plan" bodyClassName="px-5 py-1">
          {loadingSubscription ? (
            <div className="py-4">
              <LoadingRows rows={4} />
            </div>
          ) : subscription ? (
            <dl>
              <DataRow label="Plan" value={activeBase?.planName ?? 'None'} />
              <DataRow
                label="Status"
                value={
                  <StatusPill tone={SUBSCRIPTION_TONE[subscription.status]}>
                    {SUBSCRIPTION_LABEL[subscription.status]}
                  </StatusPill>
                }
              />
              <DataRow
                label="Current period"
                value={`${formatDay(subscription.currentPeriodStart)} — ${formatDay(subscription.currentPeriodEnd)}`}
                mono
              />
              <DataRow
                label="Recurring total"
                value={formatMoney(
                  subscription.billingCycle === 'yearly'
                    ? subscription.totalYearly
                    : subscription.totalMonthly,
                  currency
                )}
                hint={subscription.billingCycle === 'yearly' ? 'per year' : 'per month'}
                mono
              />
            </dl>
          ) : (
            <p className="py-4 text-sm text-muted-foreground">
              No subscription yet. Choose a plan below to get started.
            </p>
          )}
        </Panel>

        <Panel
          title={subscription ? 'Change plan' : 'Choose a plan'}
          description={
            subscription
              ? `Switching takes effect immediately, is prorated against the current period, and keeps your ${subscription.billingCycle} billing cycle and add-ons.`
              : 'You will be taken to a secure Stripe checkout to complete the first payment.'
          }
          bodyClassName="p-0"
        >
          {loadingPlans ? (
            <div className="p-5">
              <LoadingRows rows={3} />
            </div>
          ) : (
            <ul className="divide-y divide-border">
              {basePlans.map((plan) => {
                const isCurrent = activeBase?.planId === plan.id;
                return (
                  <li
                    key={plan.id}
                    className="flex flex-wrap items-center justify-between gap-4 px-5 py-4"
                  >
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <h3 className="text-sm font-semibold">{plan.name}</h3>
                        {isCurrent && (
                          <StatusPill tone="good" className="px-2 py-0.5">
                            Current
                          </StatusPill>
                        )}
                      </div>
                      {plan.description && (
                        <p className="mt-1 max-w-[65ch] text-sm text-muted-foreground">
                          {plan.description}
                        </p>
                      )}
                      <p className="mt-1.5 text-xs text-muted-foreground">
                        {plan.maxUsers > 0 ? `Up to ${plan.maxUsers} users` : 'Unlimited users'}
                        {plan.maxStorageGb > 0 ? ` · ${plan.maxStorageGb} GB storage` : ''}
                      </p>
                    </div>
                    <div className="flex items-center gap-4">
                      <span className="font-mono text-sm font-semibold tabular-nums">
                        {formatMoney(price(plan), plan.currency ?? currency)}
                        <span className="ml-1 font-sans text-xs font-normal text-muted-foreground">
                          /{cycle === 'yearly' ? 'yr' : 'mo'}
                        </span>
                      </span>
                      <Button
                        size="sm"
                        variant={isCurrent ? 'outline' : 'default'}
                        disabled={isCurrent || busy}
                        onClick={() =>
                          subscription && subscription.status !== 'cancelled'
                            ? changePlan.mutate(plan.id)
                            : checkout.mutate(plan.id)
                        }
                      >
                        {busy ? (
                          <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                        ) : isCurrent ? (
                          'Current plan'
                        ) : subscription && subscription.status !== 'cancelled' ? (
                          'Switch'
                        ) : (
                          'Choose'
                        )}
                      </Button>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </Panel>

        {subscription && addonPlans.length > 0 && (
          <Panel
            title="Add-ons"
            description="Extra capability on top of your base plan, billed on the same cycle."
            bodyClassName="p-0"
          >
            <ul className="divide-y divide-border">
              {addonPlans.map((plan) => {
                const active = activeAddonPlanIds.has(plan.id);
                const item = activeAddons.find((entry) => entry.planId === plan.id);
                return (
                  <li
                    key={plan.id}
                    className="flex flex-wrap items-center justify-between gap-4 px-5 py-4"
                  >
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <h3 className="text-sm font-semibold">{plan.name}</h3>
                        {active && (
                          <span className="inline-flex items-center gap-1 text-xs font-medium text-success">
                            <Check className="h-3.5 w-3.5" aria-hidden="true" />
                            Active
                          </span>
                        )}
                      </div>
                      {plan.description && (
                        <p className="mt-1 max-w-[65ch] text-sm text-muted-foreground">
                          {plan.description}
                        </p>
                      )}
                    </div>
                    <div className="flex items-center gap-4">
                      <span className="font-mono text-sm tabular-nums">
                        {formatMoney(price(plan), plan.currency ?? currency)}
                      </span>
                      {active && item ? (
                        <Button
                          size="sm"
                          variant="outline"
                          disabled={removeAddon.isPending}
                          onClick={() => removeAddon.mutate(item.id)}
                        >
                          <X className="h-4 w-4" aria-hidden="true" />
                          Remove
                        </Button>
                      ) : (
                        <Button
                          size="sm"
                          variant="outline"
                          disabled={addAddon.isPending}
                          onClick={() => addAddon.mutate(plan.id)}
                        >
                          <Plus className="h-4 w-4" aria-hidden="true" />
                          Add
                        </Button>
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
          </Panel>
        )}

        <Panel
          title="Other options"
          description="Invoices, tax details, and past receipts are also available in Stripe's billing portal."
          action={
            <Button
              variant="outline"
              size="sm"
              onClick={() => openPortal.mutate()}
              disabled={openPortal.isPending}
            >
              {openPortal.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
              ) : (
                <ExternalLink className="h-4 w-4" aria-hidden="true" />
              )}
              Billing portal
            </Button>
          }
        >
          {subscription && subscription.status !== 'cancelled' ? (
            confirmingCancel ? (
              <Notice
                tone="bad"
                title="Cancel this subscription?"
                action={
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      variant="destructive"
                      onClick={() => cancel.mutate()}
                      disabled={cancel.isPending}
                    >
                      {cancel.isPending ? (
                        <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                      ) : (
                        'Cancel subscription'
                      )}
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => setConfirmingCancel(false)}>
                      Keep it
                    </Button>
                  </div>
                }
              >
                Access continues until {formatDay(subscription.currentPeriodEnd)}. No refund is
                issued for the current period.
              </Notice>
            ) : (
              <div className="flex flex-wrap items-center justify-between gap-4">
                <p className="text-sm text-muted-foreground">
                  Cancelling keeps your access until {formatDay(subscription.currentPeriodEnd)}.
                </p>
                <Button size="sm" variant="ghost" onClick={() => setConfirmingCancel(true)}>
                  Cancel subscription
                </Button>
              </div>
            )
          ) : (
            <p className="text-sm text-muted-foreground">
              There is no active subscription to cancel.
            </p>
          )}
        </Panel>
      </div>
    </>
  );
}
